/**
 * @group Network
 * @summary HTTP headers for API responses
 * 
 * @remarks
 * This constant contains standard HTTP headers used in API responses
 * for consistent header management across the application.
 * 
 * @example
 * ```typescript
 * import { HTTP_HEADERS } from '@shared/constants';
 * 
 * return new Response(data, {
 *   status: 200,
 *   headers: {
 *     [HTTP_HEADERS.CONTENT_TYPE]: HTTP_HEADERS.CONTENT_TYPES.JSON,
 *   },
 * });
 * ```
 */
export const HTTP_HEADERS = {
  // Common header names
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
  CACHE_CONTROL: 'Cache-Control',
  ETAG: 'ETag',
  LAST_MODIFIED: 'Last-Modified',
  LOCATION: 'Location',
  SET_COOKIE: 'Set-Cookie',
  X_FORWARDED_FOR: 'X-Forwarded-For',
  X_REAL_IP: 'X-Real-IP',
  X_REQUEST_ID: 'X-Request-ID',
  X_PHILA_SIGNATURE: 'X-Phila-Signature',
  
  // Content types
  CONTENT_TYPES: {
    JSON: 'application/json',
    HTML: 'text/html',
    TEXT: 'text/plain',
    XML: 'application/xml',
    FORM_URLENCODED: 'application/x-www-form-urlencoded',
    MULTIPART_FORM_DATA: 'multipart/form-data',
    OCTET_STREAM: 'application/octet-stream',
    JAVASCRIPT: 'application/javascript',
    CSS: 'text/css',
    CSV: 'text/csv',
    PDF: 'application/pdf',
    ZIP: 'application/zip',
    PNG: 'image/png',
    JPEG: 'image/jpeg',
    GIF: 'image/gif',
    SVG: 'image/svg+xml',
  },
  
  // Cache control values
  CACHE_CONTROL_VALUES: {
    NO_CACHE: 'no-cache',
    NO_STORE: 'no-store',
    PRIVATE: 'private',
    PUBLIC: 'public',
    MAX_AGE: 'max-age',
    MUST_REVALIDATE: 'must-revalidate',
    PROXY_REVALIDATE: 'proxy-revalidate',
  },
} as const;
